"use client";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "ui/form";
import { z } from "zod";
import Link from "next/link";
import { cn } from "~/utils";
import { Input } from "ui/input";
import { Button } from "ui/button";
import { Checkbox } from "ui/checkbox";
import { login } from "~/actions/auth";
import { signIn } from "next-auth/react";
import { useForm } from "react-hook-form";
import { LoginSchema } from "~/schema/auth";
import { ALERT_DURATION } from "~/constants";
import GoogleLogo from "../icons/google-logo";
import FramerButton from "../ui/framer-button";
import { zodResolver } from "@hookform/resolvers/zod";
import { AnimatePresence, motion } from "framer-motion";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useState, useTransition } from "react";
import { X, XCircle } from "lucide-react";
import PasswordToggle from "../ui/password-toggle";

const LoginForm = () => {
  const [isLoading, startTransition] = useTransition();
  const searchParams = useSearchParams();
  const router = useRouter();
  const [status, setStatus] = useState("");
  const [message, setMessage] = useState("");
  const [inputType, setInputType] = useState<"password" | "text">("password");
  const callbackUrl = searchParams.get("redirect");
  const form = useForm<z.infer<typeof LoginSchema>>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  async function onSubmit(values: z.infer<typeof LoginSchema>) {
    const validatedFields = LoginSchema.safeParse(values);
    if (!validatedFields.success) return;
    startTransition(async () => {
      await login(validatedFields.data).then(async (res) => {
        if (res.success) {
          await signIn("credentials", {
            redirect: false,
            email: values.email,
            password: values.password,
          });
          setStatus("success");
          setMessage(res.message);
          setTimeout(() => {
            const redirectUrl =
              res.data?.workspaces?.length === 0
                ? "/create-workspace"
                : callbackUrl
                  ? `/select-workspace?redirect=${encodeURIComponent(callbackUrl)}`
                  : "/";
            if (res.data?.workspaces?.length === 0) {
              router.push("/create-workspace");
            } else {
              router.push(redirectUrl);
            }
          }, 500);
        } else {
          setStatus("error");
          setMessage(res.message);
        }
      });
    });
  }

  useEffect(() => {
    let timer: NodeJS.Timeout | null;
    clearTimeout(timer!);
    if (status) {
      document.body.style.setProperty(
        "--popup_duration",
        ALERT_DURATION + "ms",
      );
      timer = setTimeout(() => {
        setStatus("");
      }, ALERT_DURATION + 1000);
    } else {
      timer = null;
    }
    return () => {
      document.body.style.removeProperty("--popup_duration");
      clearTimeout(timer!);
    };
  }, [status]);
  return (
    <div className="flex w-full flex-col gap-[24px]">
      <div className="mb-[8px] flex w-full flex-col gap-[8px]">
        <h4 className="text-[24px] leading-[38px] font-[600]">Log In</h4>
        <p className="text-[#6F7777]">
          Pick up where you left off - access your dashboard, review progress,
          and keep moving forward.
        </p>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
          <div className="flex flex-col gap-[24px]">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-[8px]">
                  <FormLabel
                    htmlFor="email"
                    className={cn(
                      "text-[16px] leading-[21px] font-[500] text-[#6F7777]",
                    )}
                  >
                    Email address
                  </FormLabel>
                  <FormControl>
                    <div className="relative flex w-full items-center">
                      <Input
                        disabled={isLoading}
                        {...field}
                        type="email"
                        name="email"
                        id="email"
                        placeholder="user@example.com"
                        className="text-accent-black h-[48px] w-full rounded-[8px] border bg-transparent px-[16px] font-medium placeholder:font-light placeholder:text-gray-400/80"
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-[8px]">
                  <FormLabel
                    htmlFor="password"
                    className={cn(
                      "text-[16px] leading-[21px] font-[500] text-[#6F7777]",
                    )}
                  >
                    <span>Password</span>
                    <span
                      className={cn(
                        "flex items-center gap-x-1 text-xs opacity-0 transition-opacity duration-500 md:text-sm",
                        form.getValues("password").length &&
                          form.watch("password") !==
                            form.getValues("password") &&
                          "opacity-100",
                      )}
                    >
                      <XCircle size="24" className="text-red-600" />
                      Incorrect pin
                    </span>
                  </FormLabel>
                  <FormControl>
                    <div className="relative flex w-full items-center">
                      <Input
                        disabled={isLoading}
                        {...field}
                        type={inputType}
                        placeholder="Enter your password"
                        name="password"
                        id="password"
                        className="text-accent-black h-[48px] w-full rounded-[8px] border bg-transparent px-[16px] font-medium placeholder:font-light placeholder:text-gray-400/80"
                      />
                      <PasswordToggle
                        inputType={inputType}
                        onAction={() =>
                          setInputType((prev) =>
                            prev === "password" ? "text" : "password",
                          )
                        }
                      />
                    </div>
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="rememberMe"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="flex items-center gap-[8px]">
                      <Checkbox
                        disabled={isLoading}
                        checked={field.value}
                        name="rememberInfo"
                        id="rememberInfo"
                        className="size-[24px] cursor-pointer opacity-80"
                        onCheckedChange={field.onChange}
                      />
                      <FormLabel
                        htmlFor="rememberInfo"
                        className={cn(
                          "text-[12px] leading-[16px] font-[500] text-[#6F7777]",
                        )}
                      >
                        Remember Me
                      </FormLabel>
                    </div>
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="relative flex transform-gpu flex-col items-center gap-y-3 overflow-y-clip [perspective:300px] max-sm:w-full">
              <AnimatePresence>
                {status !== "" && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className={cn(
                      "relative flex w-full flex-col rounded-md border-l-4 px-4 py-2 text-sm font-medium",
                      status === "success"
                        ? "border-green-500 bg-green-500/10 text-green-600"
                        : "border-red-500 bg-red-500/10 text-red-600",
                    )}
                  >
                    <p className="font-bold">
                      Account Login{" "}
                      {status === "success" ? "successful!" : "failed"}
                    </p>
                    <p>{status === "success" ? " Redirecting..." : message}</p>
                    <Button
                      type="button"
                      variant={"ghost"}
                      onClick={() => setStatus("")}
                      className="absolute top-0 right-1 size-5 !p-0.5"
                    >
                      <X />
                    </Button>
                    <span
                      className={cn(
                        "bg-opacity-80 absolute bottom-0 left-0 h-1 w-full max-w-[99%] rounded-full bg-current",
                        status !== "" ? "no-width" : "w-full",
                      )}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
              <FramerButton
                isLoading={isLoading}
                text="Log in"
                className="h-[56px] cursor-pointer rounded-[12px]"
              />
            </div>
          </div>

          <div className="flex justify-center">
            <Link
              href="/forgot-password"
              className="mt-[24px] text-[14px] leading-[18px] font-[700] text-[#6EAEC9]"
            >
              Forgot password?
            </Link>
          </div>
        </form>
      </Form>
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-gray-300 dark:border-gray-600" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-white px-2 text-gray-500 dark:bg-black dark:text-gray-400">
            or
          </span>
        </div>
      </div>
      <div className="text-accent-black-2 space-y-4">
        <Button
          variant="outline"
          className="h-10 w-full cursor-pointer gap-x-3"
          onClick={() => signIn("google", { redirectTo: "/select-workspace" })}
        >
          <GoogleLogo />
          <span className="h-full w-px bg-gray-300 dark:bg-gray-600" />
          <span>Continue with Google</span>
        </Button>
      </div>
      <p className="text-accent-black-2 flex w-full items-center justify-center gap-x-2 text-center text-sm">
        Not registered yet?
        <Link
          href="/signup"
          className="text-sm font-bold text-[#3EACD4] hover:underline"
        >
          Create an Account
        </Link>
      </p>
    </div>
  );
};

export default LoginForm;
